import { Button, IconButton, List, ListItem, Modal, Typography, Box, useMediaQuery, useTheme, } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Close, Check, tpTheme } from "@trainingpeaks/designtokens";

const baseModalStyle = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  border: "none",
  borderRadius: 2,
  boxShadow: 24,
};
interface Feature {
  title: string;
  isNew: boolean;
  subtitle: string;
}

const winbackFeatureList: Feature[] = [
  {
    title: "TrainingPeaks Virtual",
    isNew: true,
    subtitle: "Ride inside with our virtual cycling experience",
  },
  {
    title: "Strength Builder",
    isNew: true,
    subtitle: "Create and save strength workouts",
  },
  {
    title: "Metrics Charts",
    isNew: true,
    subtitle: "Analyze metrics from your synced devices",
  },
  {
    title: "Performance Metrics",
    isNew: false,
    subtitle: "Fitness (CTL), Fatigue (ATL) and Form (TSB)",
  },
  {
    title: "Peak Performances",
    isNew: false,
    subtitle: "Track and compare your best efforts",
  },
];

interface WinbackModalProps {
  isOpen: boolean;
  onRedeem: () => void;
  onXClose: () => void;
  onConfirmCancel: () => void;
  isProcessing: boolean;
  error: string | null;
}

const WinbackModal = ({ isOpen, onRedeem, onXClose, onConfirmCancel, isProcessing, error }: WinbackModalProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isNarrow = useMediaQuery(theme.breakpoints.down("sm"));
  const modalStyle = { ...baseModalStyle, width: isNarrow ? "95%" : "60%" };

  const largeScreenButtonGroup = <div className="flex justify-end items-center gap-4 mt-6">
    {error && (
      <Typography variant="body2" color="error">{t(error)}</Typography>
    )}
    <Button variant="text" color="primary" onClick={onConfirmCancel} disabled={isProcessing}>
      {t("Cancel Subscription")}
    </Button>
    <Button
      variant="contained"
      color="primary"
      onClick={onRedeem}
      disabled={isProcessing}
    >
      {t("Renew Subscription")}
    </Button>
  </div>;

  const smallScreenButtonGroup = <div className="flex flex-col justify-around items-center w-full h-24 mt-4">
    {error && (
      <Typography variant="body2" color="error">{t(error)}</Typography>
    )}
    <Button
      variant="contained"
      color="primary"
      onClick={onRedeem}
      disabled={isProcessing}
      className="rounded-full px-6 w-full"
    >
      {t("Renew Subscription")}
    </Button>
    <Button
      variant="text"
      color="primary"
      className="w-full"
      onClick={onConfirmCancel}
      disabled={isProcessing}
    >
      {t("Cancel Subscription")}
    </Button>
  </div>;

  return (
    <Modal
      open={isOpen}
    >
      <Box sx={modalStyle} data-testid="winback-modal">
        <Box
          className="flex flex-col md:flex-row"
        >
          {!isMobile &&
            <Box
              component="img"
              src="https://assets.trainingpeaks.com/images/athletes/stay_focused_15.png"
              className="w-2/5 rounded-t-lg md:rounded-l-lg md:rounded-tr-none"
              alt="Athlete staying focused on training"
            >
            </Box>

          }
          <Box
            className="w-full md:w-3/5 flex flex-col px-4 pb-4"
          >
            <div className="flex justify-end">
              <IconButton
                onClick={onXClose}
                aria-label={t("Close")}
                className="px-0"
              >
                <Close
                  size="24"
                  fill={tpTheme.semanticLayer.colors.disabled.main}
                />
              </IconButton>
            </div>
            <div
              className="flex"
            >
              <Typography
                variant="h1"
                className="font-bold mb-6 p-0 text-base whitespace-pre-line leading-none md:text-3xl md:pr-8"
              >
                {t("Maximize Your Premium Subscription \n Before You Go!")}
              </Typography>
            </div>
            <List className="flex-1 py-0" disablePadding>
              {winbackFeatureList.map((feature) => (
                <ListItem
                  key={feature.title}
                  className="px-0 py-1.5 items-start gap-3"
                  disablePadding
                >
                  <Check
                    size="24"
                    fill={tpTheme.semanticLayer.colors.primary.main}
                  />
                  <div>
                    <Typography variant="body1" component="span">
                      {t(feature.title)}
                      {feature.isNew && (
                        <>
                          {" "}
                          <Typography component="span" className="text-primary-main">
                            {t("NEW!")}
                          </Typography>
                        </>
                      )}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" display="block">
                      {t(feature.subtitle)}
                    </Typography>
                  </div>
                </ListItem>
              ))}
            </List>
            {isMobile || isNarrow
              ? smallScreenButtonGroup
              : largeScreenButtonGroup
            }
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default WinbackModal;
